import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { UploadStage } from "@/components/tape/UploadStage";
import { ReviewStage } from "@/components/tape/ReviewStage";
import { ExportStage } from "@/components/tape/ExportStage";
import { AppHeader } from "@/components/AppHeader";
import { OnboardingNameModal } from "@/components/OnboardingNameModal";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Stage, UploadedFile, SessionMeta } from "@/lib/tape";

export const Route = createFileRoute("/tapes")({
  head: () => ({
    meta: [
      { title: "Self Tapes — Upload, trim, and export | RoleBoard" },
      {
        name: "description",
        content:
          "Upload audition self tapes, trim them in your browser, add slates, and export submission-ready files. Built for actors.",
      },
      { property: "og:title", content: "Self Tapes — RoleBoard" },
      {
        property: "og:description",
        content:
          "Trim, slate, and export self tapes from your browser. Private by default.",
      },
      { property: "og:url", content: "https://roleboard.app/tapes" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/tapes" }],
  }),
  component: TapesPage,
});

const STEPS: { key: Stage; label: string }[] = [
  { key: "upload", label: "Upload" },
  { key: "review", label: "Review" },
  { key: "export", label: "Export" },
];

function ProgressNav({ stage }: { stage: Stage }) {
  const currentIdx = STEPS.findIndex((s) => s.key === stage);
  return (
    <nav className="flex items-center gap-2 sm:gap-3">
      {STEPS.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s.key} className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`size-6 rounded-full grid place-items-center text-[11px] font-semibold transition-all duration-300 ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : active
                      ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
              </div>
              <span
                className={`text-sm font-medium hidden sm:inline transition-colors ${
                  active
                    ? "text-foreground"
                    : done
                      ? "text-foreground/70"
                      : "text-muted-foreground"
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`h-px w-8 sm:w-12 transition-colors duration-300 ${
                  done ? "bg-primary" : "bg-border"
                }`}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

function TapesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  const [stage, setStage] = useState<Stage>("upload");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [meta, setMeta] = useState<SessionMeta>({
    firstName: "",
    lastName: "",
    projectName: "",
    roleName: "",
  });
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .maybeSingle();
      if (!active) return;
      const fn = data?.first_name ?? "";
      const ln = data?.last_name ?? "";
      setMeta((m) => ({ ...m, firstName: fn, lastName: ln }));
      setNeedsOnboarding(!fn || !ln);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  function reset() {
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
    setMeta((m) => ({
      firstName: m.firstName,
      lastName: m.lastName,
      projectName: "",
      roleName: "",
    }));
    setStage("upload");
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <OnboardingNameModal
        open={needsOnboarding}
        onComplete={(fn, ln) => {
          setMeta((m) => ({ ...m, firstName: fn, lastName: ln }));
          setNeedsOnboarding(false);
        }}
        onSkip={() => setNeedsOnboarding(false)}
      />

      <main className="container max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="mb-8 flex justify-center">
          <ProgressNav stage={stage} />
        </div>
        <div
          key={stage}
          className="animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          {stage === "upload" && (
            <UploadStage
              files={files}
              setFiles={setFiles}
              onContinue={() => setStage("review")}
            />
          )}
          {stage === "review" && (
            <ReviewStage
              files={files}
              setFiles={setFiles}
              meta={meta}
              setMeta={setMeta}
              onBack={() => setStage("upload")}
              onContinue={() => setStage("export")}
            />
          )}
          {stage === "export" && (
            <ExportStage
              files={files}
              meta={meta}
              onBack={() => setStage("review")}
              onReset={reset}
            />
          )}
        </div>
      </main>

      <footer className="border-t border-border mt-20">
        <div className="container max-w-6xl mx-auto px-5 md:px-8 py-5 text-xs text-muted-foreground flex justify-between">
          <span>RoleBoard</span>
          <span>Your personal acting workspace</span>
        </div>
      </footer>
    </div>
  );
}
